/**
 * Vouchers · Pending JV Review — accountant approval queue.
 *
 * Lists every JV drafted in `pending_review` status (currently only from the
 * GSTR-3B `mark-filed` flow) with a full 7-line Dr/Cr breakdown. Accountant
 * can approve → posts a real journal voucher via `post_voucher()` — or reject
 * with a reason so the reviewer trail is preserved.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  FileCheck, RefreshCw, ShieldCheck, XCircle, ClipboardCheck, Info,
} from "lucide-react";

export default function PendingJvReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);   // id currently being approved/rejected

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pending-jvs");
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const approve = async (row) => {
    if (!window.confirm(`Post journal voucher for ${row.period}? This is irreversible.`)) return;
    setBusy(row.id);
    try {
      const r = await api.post(`/pending-jvs/${row.id}/approve`, {});
      toast.success(`Voucher ${r.data.voucher_id.slice(0, 8)} posted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
    setBusy(null);
  };

  const reject = async (row) => {
    const reason = window.prompt("Reject reason (visible to filer):");
    if (reason === null) return;
    setBusy(row.id);
    try {
      await api.post(`/pending-jvs/${row.id}/reject`, { reason });
      toast.success("Rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
    setBusy(null);
  };

  const inr = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="pending-jv-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// ACCOUNTS · APPROVAL QUEUE</div>
          <h1 className="text-3xl font-bold mt-1 font-heading flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7 text-indigo-500" /> Pending JV Review
          </h1>
          <div className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Draft journals waiting for an accountant. Currently sourced from the GSTR-3B
            filing offset — click Approve to post a real journal voucher via the standard
            posting pipeline. Rejected drafts remain in the audit trail.
          </div>
        </div>
        <Button onClick={load} disabled={loading} variant="outline"
          className="rounded-sm gap-1.5" data-testid="pjv-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="card-sharp p-10 text-center text-muted-foreground text-sm" data-testid="pjv-empty">
          <FileCheck className="w-10 h-10 mx-auto mb-3 opacity-50" />
          No pending JVs. When you mark a GSTR-3B as filed, its offset entry lands here for review.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const totalDr = row.lines.reduce((s, l) => s + (l.debit || 0), 0);
            const totalCr = row.lines.reduce((s, l) => s + (l.credit || 0), 0);
            const balanced = Math.abs(totalDr - totalCr) < 0.01;
            return (
              <div key={row.id} className="card-sharp p-4 space-y-2"
                   data-testid={`pjv-card-${row.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-accent">
                      {row.kind === "gstr_3b_filing_jv" ? "GSTR-3B FILING OFFSET" : row.kind}
                    </div>
                    <div className="text-xl font-bold mt-0.5">Period {row.period}</div>
                    <div className="text-xs text-muted-foreground">
                      Drafted {row.created_at?.slice(0, 10)} · id {row.id.slice(0, 8)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => reject(row)} disabled={busy === row.id}
                      variant="outline" className="rounded-sm gap-1.5" data-testid={`pjv-reject-${row.id}`}>
                      <XCircle className="w-4 h-4 text-red-500" /> Reject
                    </Button>
                    <Button size="sm" onClick={() => approve(row)} disabled={busy === row.id || !balanced}
                      className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                      data-testid={`pjv-approve-${row.id}`}>
                      {busy === row.id ? <RefreshCw className="w-4 h-4 animate-spin" /> :
                                            <ShieldCheck className="w-4 h-4" />} Approve & Post
                    </Button>
                  </div>
                </div>

                <table className="w-full text-sm border-t border-white/10 mt-2">
                  <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left p-2">Narration</th>
                      <th className="text-right p-2">Debit ₹</th>
                      <th className="text-right p-2">Credit ₹</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {row.lines.map((l, idx) => (
                      <tr key={idx}>
                        <td className="p-2">{l.narration}</td>
                        <td className="p-2 text-right font-mono tabular-nums">
                          {l.debit > 0 ? inr(l.debit) : ""}
                        </td>
                        <td className="p-2 text-right font-mono tabular-nums">
                          {l.credit > 0 ? inr(l.credit) : ""}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-white/20 font-bold">
                      <td className="p-2 uppercase text-[10px] font-mono">Total</td>
                      <td className="p-2 text-right font-mono tabular-nums">{inr(totalDr)}</td>
                      <td className="p-2 text-right font-mono tabular-nums">{inr(totalCr)}</td>
                    </tr>
                  </tbody>
                </table>

                {!balanced && (
                  <div className="text-xs text-red-500 flex items-center gap-1" data-testid={`pjv-unbalanced-${row.id}`}>
                    <Info className="w-3 h-3" /> Journal is not balanced (Dr ≠ Cr) — cannot approve.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
